<template>
  <romanza-default-section :section="section" :menu="menu" menu-item-tag-location="before-title"
                           class="menu--section-dessert menu--section-romanza_dessert" :menu-item-component="menuItemComponent">
  </romanza-default-section>
</template>

<script lang="ts">
/**
 * Custom component for Romanza Dessert menu sections.
 *
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.4.0
 * @copyright  Copyright (C) 2022-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *  2022-05-25 (1.0.0) (jhoward)
 *    - Copy from Romanza_wineList.vue component.
 *    - Remove all BIN and price-type logic.
 *
 *  2023-04-17 (1.1.0) (jhoward)
 *    - Refactor <Romanza_dessert> component to use TypeScript.
 *    - <Romanza_dessert> section prop is required.
 *    - Refactor <Romanza_dessert> component to use helper computed properties.
 *    - Remove unused methods from <Romanza_dessert> component.
 *
 *    2023-08-23 (1.2.0) (jhoward)
 *      - Use <menu-version> in <Romanza_dessert>.
 *
 *    2023-11-08 (1.2.1) (jhoward)
 *      - Move <menu-tag> to the left of titles in <Romanza_dessert>.
 *      - Allow HTML in item.mi_description in <Romanza_dessert>.
 *
 *    2024-05-01 (1.2.2) (jhoward)
 *      - Add "Contains Nuts" <menu-tag> to "HOUSEMADE GELATO" subsection in <Romanza_dessert>.
 *
 *    2024-07-19 (1.2.3) (jhoward)
 *      - Rename .visual_menu--tag CSS rules to .menu--tag in <romanza-dessert>.
 *
 *    2025-02-10 (1.3.0) (jhoward)
 *      - Add <menu-schedules> to the <Romanza_dessert> component if in admin view.
 *
 *    2025-04-08 (1.4.0) (jhoward)
 *      - Use <Romanza_defaultSection> in <Romanza_dessert>.
 *      - Remove "visual_" prefix from class names in <Romanza_dessert>.
 */

import {defineComponent, PropType} from "vue";
import {ALLOWED_ITEM_COMPONENTS, Menu, MenuSection} from "../defs";
import RomanzaDefaultSection from "./Romanza_defaultSection.vue";

export default defineComponent({
  components: {
    RomanzaDefaultSection,
  },
  name: "Romanza_dessert",
  props: {
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    menu: {
      type: Object as PropType<Menu>,
      required: true
    },
    menuItemComponent: {
      type: String,
      default: 'romanza-dessert-item',
      validate: (value: string) => '' === value || ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
  },
})
</script>

<style scoped>
.menu--section-romanza_dessert {
  font-size: 0.9em;
  margin: 0 auto;
  max-width: 60%;
  padding-top: 0.5em;
}

.textual_menu--container .textual_menu--section.menu--section-romanza_dessert {
  max-width: 492px
}

.visual_menu--container-landscape .menu--section-romanza_dessert {
  padding-top: 0.5em;
}

.menu--section-romanza_dessert:deep(.menu--item) {
  text-align: initial;
}

.menu--section-romanza_dessert:deep(.menu--item_title),
.menu--section-romanza_dessert:deep(.menu--section_group_title) {
  font-size: 0.9em;
  font-weight: bold;
}

.menu--section-romanza_dessert:deep(.menu--item_description),
.menu--section-romanza_dessert:deep(.menu--section_subgroup_title) {
  font-weight: normal;
  font-size: 0.8em;
  margin-top: -0.3em;
}

.menu--section-romanza_dessert:deep(.menu--item),
.menu--section-romanza_dessert:deep(.menu--section_subgroup_title) {
  display: grid;
  grid-template-columns: 1fr 0.2fr;
  grid-gap: 0.25em;
}

.menu--section-romanza_dessert:deep(.menu--section_subgroup_title) {
  text-align: initial;
  margin-left: 0;
}

.menu--section-romanza_dessert:deep(.menu--item_subgroup + .menu--item_subgroup) {
  margin-top: 1em;
}

.menu--section-romanza_dessert:deep(.menu--item_price) {
  text-align: right;
  font-weight: bold;
}

.menu--section-romanza_dessert:deep(.visual_menu--page_section.visual_menu--page_section-bottom) {
  padding-top: 2em;
}





.menu--section-romanza_dessert:deep(.menu--item_title) {
  position: relative;
}

.menu--section-romanza_dessert:deep(.menu--section_group_title) {
  position: relative;
}

.menu--section-romanza_dessert:deep(.menu--item_title .menu--tag),
.menu--section-romanza_dessert:deep(.menu--section_group_title .menu--tag) {
  position: absolute;
  right: 100%;
  top: 0.7em;
}
</style>